'use client';

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { ClipboardList, Download, Edit, Share2, Copy } from 'lucide-react';
import { PageLoader } from '@/components/ui/page-loader';
import { useToast } from '@crm-src/hooks/use-toast';
import { invoicesApi } from '@crm-src/lib/invoices-api';
import { downloadDocumentPdf, fetchDocumentPdf } from '@crm-src/lib/document-pdf';
import { shareDocumentCompanyName, shareDocumentPdfFilename } from '@crm-src/lib/shareDocumentDefaults';
import { ShareDocumentModal } from '@crm-src/components/share-document-modal';
import { PdfReader } from '@/components/pdf/pdf-reader';
import { BackButton } from "@/components/ui/back-button";

export default function QuoteViewPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const quoteId = params.id as string;
  
  const [quote, setQuote] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (quoteId) {
      loadQuote();
    }
  }, [quoteId]);

  const loadQuote = async () => {
    try {
      setIsLoading(true);
      const apiResponse = await invoicesApi.getInvoiceById(quoteId);
      
      // Extract actual data from the API response wrapper
      const data = apiResponse.data || apiResponse; // Handle both wrapped and unwrapped responses
      
      setQuote(data);
      
      // The PDF is the one generator's (crm-api InvoicePdfRenderer), fetched —
      // never drawn here. See lib/document-pdf.ts.
      try {
        const { bytes } = await fetchDocumentPdf(quoteId);
        // Rendered via pdf.js <canvas> (the shared PdfReader), NOT a blob:
        // <iframe> — the desktop webviews have no native PDF plugin.
        setPdfBytes(bytes);
      } catch (error) {
        console.error('Error loading PDF:', error);
        toast({
          title: "Warning",
          description: "Could not load the PDF preview",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error loading quote:', error);
      toast({
        title: "Error",
        description: "Failed to load quote",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDownload = async () => {
    if (!quote) return;

    try {
      await downloadDocumentPdf(quoteId, {
        documentType: 'quote',
        number: quote.invoice_number,
        company: shareDocumentCompanyName(quote),
      });
      toast({
        title: "Success",
        description: "Quote downloaded successfully",
      });
    } catch (error: any) {
      console.error('Error downloading quote:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to download quote",
        variant: "destructive",
      });
    }
  };

  const handleEdit = () => {
    navigate(`/crm/quotes/generate?invoiceId=${quoteId}`);
  };

  const handleDuplicate = () => {
    navigate(`/crm/quotes/generate?duplicateFrom=${quoteId}`);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!quote) {
    return (
      <Box sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center", color: "text.secondary" }}>
              <Typography>Quote not found</Typography>
              <BackButton sx={{ mt: 2 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <BackButton />
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  Quote {quote.invoice_number}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {/* A reseller's accepted quote may have become a purchase order on
                    XOP Anytime (crm-api adds `reseller_quote.purchase_order`,
                    2026-09-17) — link straight to it. */}
                {quote.reseller_quote?.purchase_order && (
                  <Button
                    variant="outlined"
                    size="small"
                    data-cy="quote-purchase-order-link"
                    onClick={() => navigate(`/crm/purchase-orders/${quote.reseller_quote.purchase_order.id}`)}
                    startIcon={<ClipboardList size={16} />}
                  >
                    Purchase order {quote.reseller_quote.purchase_order.number}
                  </Button>
                )}
                <Button variant="outlined" color="inherit" size="small" onClick={handleEdit} startIcon={<Edit size={16} />}>
                  Edit
                </Button>
                <Button variant="outlined" color="inherit" size="small" onClick={handleDuplicate} startIcon={<Copy size={16} />}>
                  Duplicate
                </Button>
                <Button variant="outlined" color="inherit" size="small" onClick={handleShare} startIcon={<Share2 size={16} />}>
                  Share
                </Button>
                <Button variant="outlined" color="inherit" size="small" onClick={handleDownload} startIcon={<Download size={16} />}>
                  Download
                </Button>
              </Box>
            </Box>
          }
          subheader={quote.customer_company_name ? quote.customer_company_name : undefined}
        />
        <CardContent>
          {pdfBytes ? (
            <PdfReader
              bytes={pdfBytes}
              filename={shareDocumentPdfFilename('quote', quote.invoice_number || quote.number, shareDocumentCompanyName(quote))}
              containerTestId="quote-pdf-preview"
              fallbackTestId="quote-pdf-fallback"
              fallbackText="This quote couldn’t be previewed inline. Use Download above to view it."
            />
          ) : (
            <Box sx={{ textAlign: "center", color: "text.secondary", py: 6 }}>
              <Typography sx={{ mb: 1, fontSize: "1.125rem", fontWeight: 500 }}>Could not generate PDF preview</Typography>
              <Typography sx={{ fontSize: 14, mb: 3 }}>There may be missing data for this quote.</Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                <Button variant="contained" onClick={handleEdit} startIcon={<Edit size={16} />}>
                  Edit Quote
                </Button>
                <Button variant="outlined" color="inherit" onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Share Document Modal */}
      <ShareDocumentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        documentId={quoteId}
        documentType="quote"
        documentNumber={quote?.invoice_number}
      />
    </Box>
  );
}

